import { useState, useEffect, useRef, useMemo } from "react"
import { Doughnut, Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js"
import API from "../../../utils/API"
import Sweetalert from "../../../utils/Sweetalert"
import ButtonCollapse from "../../ButtonCollapse"
import "../AiTargetingStats.css"
import { SPINNER_DELAY_MS } from "../constants"

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const AGE_RANGE_ORDER = ["0-12", "13-17", "18-24", "25-34", "35-44", "45-54", "55-64", "65+"]

const GENDER_LABELS = { M: "Hommes", F: "Femmes" }
const GENDER_COLORS = { M: "#36A2EB", F: "#FF6384" }

function getGenderCount(genderData, key) {
  const entry = genderData.find((g) => g.gender === key)

  return entry ? entry.count : 0
}

function sortByAgeRange(rows) {
  return [...rows].sort(
    (a, b) => AGE_RANGE_ORDER.indexOf(a.ageRange) - AGE_RANGE_ORDER.indexOf(b.ageRange)
  )
}

const centerTextPlugin = {
  id: "doughnutCenterText",
  beforeDraw(chart) {
    const { ctx, chartArea } = chart
    if (!chartArea) return

    const total = chart.data.datasets[0].data.reduce((sum, v) => sum + v, 0)
    if (total === 0) return

    const centerX = (chartArea.left + chartArea.right) / 2
    const centerY = (chartArea.top + chartArea.bottom) / 2

    ctx.save()
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"

    ctx.font = "bold 22px sans-serif"
    ctx.fillStyle = "#343a40"
    ctx.fillText(total.toLocaleString("fr-FR"), centerX, centerY - 10)

    ctx.font = "12px sans-serif"
    ctx.fillStyle = "#6c757d"
    ctx.fillText("passages", centerX, centerY + 12)

    ctx.restore()
  },
}

const doughnutLabelPlugin = {
  id: "doughnutSliceLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const meta = chart.getDatasetMeta(0)
    if (!meta) return

    const dataset = chart.data.datasets[0]
    const total = dataset.data.reduce((sum, v) => sum + v, 0)
    if (total === 0) return

    ctx.save()

    for (let i = 0; i < meta.data.length; i++) {
      const value = dataset.data[i]
      if (!value) continue

      const arc = meta.data[i]
      const midAngle = (arc.startAngle + arc.endAngle) / 2
      const innerRadius = arc.innerRadius
      const outerRadius = arc.outerRadius
      const midRadius = (innerRadius + outerRadius) / 2
      const x = arc.x + Math.cos(midAngle) * midRadius
      const y = arc.y + Math.sin(midAngle) * midRadius
      const pct = Math.round((value / total) * 100)

      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillStyle = "#fff"
      ctx.font = "bold 12px sans-serif"
      ctx.fillText(`${pct}%`, x, y)
    }

    ctx.restore()
  },
}

function GenderDoughnut({ genderData }) {
  const maleCount = getGenderCount(genderData, "M")
  const femaleCount = getGenderCount(genderData, "F")
  const total = maleCount + femaleCount

  const chartData = useMemo(
    () => ({
      labels: ["Hommes", "Femmes"],
      datasets: [
        {
          data: [maleCount, femaleCount],
          backgroundColor: [GENDER_COLORS.M, GENDER_COLORS.F],
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 6,
        },
      ],
    }),
    [maleCount, femaleCount]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "65%",
      plugins: {
        legend: {
          position: "bottom",
          reverse: true,
          labels: { padding: 16, usePointStyle: true, pointStyle: "circle" },
        },
        tooltip: {
          callbacks: {
            label: (item) => {
              const pct = total > 0 ? Math.round((item.raw / total) * 100) : 0
              return `${item.label}: ${item.raw.toLocaleString("fr-FR")} (${pct}%)`
            },
          },
        },
      },
    }),
    [total]
  )

  if (total === 0) {
    return (
      <div className="ai-stats-empty" style={{ height: 240 }}>
        <div className="ai-stats-empty__inner">
          <p className="ai-stats-empty__text">Aucune donnée</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ height: 240 }}>
      <Doughnut data={chartData} options={options} plugins={[centerTextPlugin, doughnutLabelPlugin]} />
    </div>
  )
}

const GENDER_FILTER_OPTIONS = [
  { key: null, label: "Tous" },
  { key: "M", label: "Hommes" },
  { key: "F", label: "Femmes" },
]

const GENDER_BAR_STYLES = {
  null: { bg: "rgba(108, 117, 125, 0.65)", border: "rgba(108, 117, 125, 1)" },
  M: { bg: "rgba(54, 162, 235, 0.7)", border: "rgba(54, 162, 235, 1)" },
  F: { bg: "rgba(255, 99, 132, 0.7)", border: "rgba(255, 99, 132, 1)" },
}

const barValuePlugin = {
  id: "barValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const meta = chart.getDatasetMeta(0)
    if (!meta || meta.hidden) return

    const dataset = chart.data.datasets[0].data
    const total = dataset.reduce((sum, v) => sum + v, 0)
    if (total === 0) return

    ctx.save()
    ctx.font = "bold 11px sans-serif"
    ctx.textBaseline = "middle"

    for (const bar of meta.data) {
      const value = dataset[meta.data.indexOf(bar)]
      if (!value) continue

      const pct = Math.round((value / total) * 100)
      const label = `${pct}%`
      const textWidth = ctx.measureText(label).width
      const barWidth = bar.width
      const fitsInside = textWidth + 12 < barWidth

      if (fitsInside) {
        ctx.fillStyle = "#fff"
        ctx.textAlign = "right"
        ctx.fillText(label, bar.x - 6, bar.y)
      } else {
        ctx.fillStyle = "#495057"
        ctx.textAlign = "left"
        ctx.fillText(label, bar.x + 4, bar.y)
      }
    }

    ctx.restore()
  },
}

const yAxisHorizontalTitlePlugin = {
  id: "yAxisHorizontalTitle",
  afterDraw(chart) {
    const yScale = chart.scales.y
    if (!yScale) return

    const titleOpts = yScale.options.title
    if (!titleOpts?.text) return

    const { ctx } = chart
    const font = titleOpts.font || {}

    ctx.save()
    ctx.font = `${font.weight || "600"} ${font.size || 12}px sans-serif`
    ctx.fillStyle = titleOpts.color || "#495057"
    ctx.textAlign = "left"
    ctx.textBaseline = "bottom"
    ctx.fillText(titleOpts.text, yScale.left, yScale.top - 12)
    ctx.restore()
  },
}

function deriveAgeRangeByGender(genderAgeData, gender) {
  const countByAge = new Map()

  for (const row of genderAgeData) {
    if (row.gender !== gender) continue

    countByAge.set(row.ageRange, (countByAge.get(row.ageRange) || 0) + row.count)
  }

  return [...countByAge.entries()].map(([ageRange, count]) => ({ ageRange, count }))
}

function AgeRangeBar({ ageRangeData, genderAgeData }) {
  const [genderFilter, setGenderFilter] = useState(null)

  const displayData = useMemo(() => {
    if (!genderFilter) return ageRangeData

    return deriveAgeRangeByGender(genderAgeData, genderFilter)
  }, [ageRangeData, genderAgeData, genderFilter])

  const sorted = useMemo(() => sortByAgeRange(displayData), [displayData])
  const total = useMemo(() => sorted.reduce((sum, r) => sum + r.count, 0), [sorted])

  const barStyle = GENDER_BAR_STYLES[genderFilter] || GENDER_BAR_STYLES[null]

  const chartData = useMemo(
    () => ({
      labels: sorted.map((r) => r.ageRange),
      datasets: [
        {
          label: "Passages",
          data: sorted.map((r) => r.count),
          backgroundColor: barStyle.bg,
          borderColor: barStyle.border,
          borderWidth: 1,
          borderRadius: 3,
        },
      ],
    }),
    [sorted, barStyle]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      layout: { padding: { top: 25 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => {
              const pct = total > 0 ? Math.round((item.raw / total) * 100) : 0
              return `${item.raw.toLocaleString("fr-FR")} (${pct}%)`
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 11 } },
          grid: { color: "rgba(0,0,0,0.05)" },
          title: { display: true, align: "end", text: "Nombre de passages", font: { size: 12, weight: "600" }, color: "#6c757d" },
        },
        y: {
          ticks: { font: { size: 12 } },
          grid: { display: false },
          title: { display: false, text: "Tranche d'âge", font: { size: 12 }, color: "#6c757d" },
        },
      },
    }),
    [total]
  )

  return (
    <>
      <div className="d-flex justify-content-center mb-2" role="group" aria-label="Filtre par genre">
        {GENDER_FILTER_OPTIONS.map(({ key, label }) => (
          <button
            key={label}
            type="button"
            className={`btn btn-sm ${genderFilter === key ? "btn-primary" : "btn-outline-secondary"} mx-1`}
            onClick={() => setGenderFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {total === 0 ? (
        <div className="ai-stats-empty" style={{ height: 240 }}>
          <div className="ai-stats-empty__inner">
            <p className="ai-stats-empty__text">Aucune donnée</p>
          </div>
        </div>
      ) : (
        <div style={{ height: 240 }}>
          <Bar data={chartData} options={options} plugins={[barValuePlugin, yAxisHorizontalTitlePlugin]} />
        </div>
      )}
    </>
  )
}

function GenderAgeHeatmap({ genderAgeData }) {
  const { map, maxCount } = useMemo(() => {
    const m = new Map()
    let max = 0

    for (const { gender, ageRange, count } of genderAgeData) {
      m.set(`${gender}-${ageRange}`, count)
      if (count > max) max = count
    }

    return { map: m, maxCount: max }
  }, [genderAgeData])

  const hasData = genderAgeData.length > 0

  function heatmapCellColor(count) {
    if (!count || maxCount === 0) return { backgroundColor: "#f9fafb" }

    const ratio = count / maxCount

    return {
      backgroundColor: `rgba(108, 99, 255, ${0.08 + ratio * 0.82})`,
      color: ratio > 0.55 ? "#fff" : "#1f2937",
    }
  }

  if (!hasData) {
    return (
      <div className="ai-stats-empty" style={{ height: 100 }}>
        <div className="ai-stats-empty__inner">
          <p className="ai-stats-empty__text">Aucune donnée</p>
        </div>
      </div>
    )
  }

  return (
    <div className="table-responsive">
      <table className="ai-stats-heatmap__table">
        <thead>
          <tr>
            <th className="ai-stats-heatmap__corner"></th>
            {AGE_RANGE_ORDER.map((range) => (
              <th key={range} className="ai-stats-heatmap__col-header">
                {range}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {["M", "F"].map((gender) => (
            <tr key={gender}>
              <th className="ai-stats-heatmap__row-header">{GENDER_LABELS[gender]}</th>
              {AGE_RANGE_ORDER.map((range) => {
                const count = map.get(`${gender}-${range}`) || 0
                return (
                  <td
                    key={range}
                    className="ai-stats-heatmap__cell"
                    style={heatmapCellColor(count)}
                    title={`${GENDER_LABELS[gender]} ${range} : ${count} passage${count > 1 ? "s" : ""}`}
                  >
                    {count > 0 ? count.toLocaleString("fr-FR") : ""}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function DemographicsCharts({ queryParams }) {
  const [data, setData] = useState(null)
  const [showSpinner, setShowSpinner] = useState(true)
  const [showHeatmap, setShowHeatmap] = useState(false)
  const spinnerTimerRef = useRef(null)
  const hasPreviousData = useRef(false)

  useEffect(() => {
    const controller = new AbortController()

    clearTimeout(spinnerTimerRef.current)

    if (!hasPreviousData.current) {
      setShowSpinner(true)
    } else {
      spinnerTimerRef.current = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS)
    }

    API.GET(
      `/ai-targeting-stats/demographics?${queryParams}`,
      (response) => {
        clearTimeout(spinnerTimerRef.current)
        setData(response.data)
        setShowSpinner(false)
        hasPreviousData.current = true
      },
      () => {
        clearTimeout(spinnerTimerRef.current)
        Sweetalert.fireErrorToast("Impossible de charger les données démographiques")
        setData(null)
        setShowSpinner(false)
      },
      { signal: controller.signal }
    )

    return () => {
      controller.abort()
      clearTimeout(spinnerTimerRef.current)
    }
  }, [queryParams])

  const hasData =
    data &&
    ((data.gender && data.gender.length > 0) ||
      (data.ageRange && data.ageRange.length > 0) ||
      (data.genderAge && data.genderAge.length > 0))

  return (
    <div className="row">
      <div className="col">
        <div className="card card-primary card-outline">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-users mr-2"></i>
              Démographie de l'audience
            </h3>
            <div className="card-tools">
              <ButtonCollapse />
            </div>
          </div>
          <div className="card-body" style={{ display: "block" }}>
            {showSpinner ? (
              <div className="ai-stats-spinner" style={{ height: 300 }}>
                <i className="fas fa-2x fa-sync-alt fa-spin"></i>
              </div>
            ) : !hasData ? (
              <div className="ai-stats-empty" style={{ height: 300 }}>
                <div className="ai-stats-empty__inner">
                  <i className="fas fa-users ai-stats-empty__icon"></i>
                  <p className="ai-stats-empty__text">Aucune donnée disponible pour cette période.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="ai-stats-info-banner mb-3">
                  <i className="fas fa-lightbulb ai-stats-info-banner__icon ai-stats-info-banner__icon--tip"></i>
                  <small>
                    Utilisez ces données pour ajuster vos critères de ciblage (genre, tranche d'âge) en fonction de l'audience réelle détectée.
                  </small>
                </div>

                <div className="row align-items-stretch">
                  <div className="col-lg-4 col-md-5 mb-3 d-flex flex-column">
                    <h5 className="ai-stats-chart-label text-center mb-3">
                      <i className="fas fa-venus-mars mr-1"></i> Répartition par genre
                    </h5>
                    <div className="d-flex flex-column flex-grow-1 justify-content-end">
                      <GenderDoughnut genderData={data.gender || []} />
                    </div>
                  </div>
                  <div className="col-lg-8 col-md-7 mb-3 d-flex flex-column">
                    <h5 className="ai-stats-chart-label text-center mb-3">
                      <i className="fas fa-users mr-1"></i> Répartition par tranche d'âge
                    </h5>
                    <div className="d-flex flex-column flex-grow-1 justify-content-end">
                      <AgeRangeBar
                        ageRangeData={data.ageRange || []}
                        genderAgeData={data.genderAge || []}
                      />
                    </div>
                  </div>
                </div>

                <div className="ai-stats-doughnut__divider">
                  <span>Analyse croisée</span>
                </div>

                <div className="text-center mb-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowHeatmap((prev) => !prev)}
                  >
                    <i className={`fas fa-${showHeatmap ? "chevron-up" : "th"} mr-1`}></i>
                    {showHeatmap ? "Masquer la matrice" : "Voir la matrice genre × tranche d'âge"}
                  </button>
                </div>

                {showHeatmap && (
                  <>
                    <h6 className="ai-stats-chart-label text-center mb-3">
                      <i className="fas fa-th mr-1"></i> Croisement genre × tranche d'âge
                    </h6>
                    <GenderAgeHeatmap genderAgeData={data.genderAge || []} />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
